/**
 * hk_os · voiceCache
 * ────────────────────────────────────────────────────────────────────
 * Thin wrapper over the browser Cache API for the Operational Assistant
 * voice pack. No Service Worker required — the Cache API is available
 * in every evergreen browser and lets a warm-up manager download every
 * audio asset once, then serve them offline via `matchAudio(url)`.
 *
 * This module is pure infrastructure — no React, no Bengali text, no
 * business logic. It backs `<VoiceWarmupManager>` and `<VoicePlayer>`.
 */

const CACHE_NAME = "hk-os-voice-v1";
const LS_MANIFEST_KEY = "hk-os-voice-manifest";
const LS_MANIFEST_META_KEY = "hk-os-voice-manifest-meta";

const isBrowser = () => typeof window !== "undefined" && "caches" in window;

export async function openCache() {
  if (!isBrowser()) return null;
  try {
    return await window.caches.open(CACHE_NAME);
  } catch {
    return null;
  }
}

/** Persist the manifest JSON (metadata only, no audio) to localStorage. */
export function saveManifest(manifest) {
  try {
    window.localStorage.setItem(LS_MANIFEST_KEY, JSON.stringify(manifest));
    window.localStorage.setItem(
      LS_MANIFEST_META_KEY,
      JSON.stringify({
        version: manifest.version,
        checksum: manifest.checksum,
        locale: manifest.locale,
        saved_at: Date.now(),
      })
    );
  } catch {
    /* localStorage quota — non-fatal */
  }
}

export function readManifest() {
  try {
    const raw = window.localStorage.getItem(LS_MANIFEST_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function readManifestMeta() {
  try {
    const raw = window.localStorage.getItem(LS_MANIFEST_META_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearManifest() {
  try {
    window.localStorage.removeItem(LS_MANIFEST_KEY);
    window.localStorage.removeItem(LS_MANIFEST_META_KEY);
  } catch { /* noop */ }
}

/** Download a single audio URL into the Cache API. Returns true if cached. */
export async function cacheAudio(url, absoluteBase) {
  const cache = await openCache();
  if (!cache) return false;
  const fullUrl = url.startsWith("http") ? url : `${absoluteBase}${url}`;
  try {
    const existing = await cache.match(fullUrl);
    if (existing) return true;
    const resp = await fetch(fullUrl, { credentials: "include" });
    if (!resp.ok) return false;
    await cache.put(fullUrl, resp.clone());
    return true;
  } catch {
    return false;
  }
}

/** Return a Response for a cached audio URL (or null). */
export async function matchAudio(url, absoluteBase) {
  const cache = await openCache();
  if (!cache) return null;
  const fullUrl = url.startsWith("http") ? url : `${absoluteBase}${url}`;
  try {
    return (await cache.match(fullUrl)) || null;
  } catch {
    return null;
  }
}

/** Number of cached audio entries (best-effort). */
export async function countCached() {
  const cache = await openCache();
  if (!cache) return 0;
  try {
    const keys = await cache.keys();
    return keys.length;
  } catch {
    return 0;
  }
}

export async function clearCache() {
  if (!isBrowser()) return;
  try {
    await window.caches.delete(CACHE_NAME);
  } catch { /* noop */ }
}
